import { useState, useEffect } from "react";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import Welcome from "@/sections/Welcome";
import Navbar from "@/sections/Navbar";
import Hero from "@/sections/Hero";
import About from "@/sections/About";
import Services from "@/sections/Services";
import Documents from "@/sections/Documents";
import Pricing from "@/sections/Pricing";
import FAQ from "@/sections/FAQ";
import Contact from "@/sections/Contact";
import Newsletter from "@/sections/Newsletter";
import Footer from "@/sections/Footer";

const LOGO_IMG = "https://customer-assets.emergentagent.com/job_site-creator-2145/artifacts/d1n4bcn4_13880.jpg";

const sections = [
  { id: "home", label: "Home" },
  { id: "about", label: "About" },
  { id: "services", label: "Services" },
  { id: "documents", label: "Documents" },
  { id: "pricing", label: "Pricing" },
  { id: "faq", label: "FAQ" },
  { id: "contact", label: "Contact" },
  { id: "newsletter", label: "Newsletter" },
];

function useActiveSection() {
  const [active, setActive] = useState("home");

  useEffect(() => {
    const els = sections.map((s) => document.getElementById(s.id)).filter(Boolean);
    if (!els.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { threshold: 0.25, rootMargin: "-60px 0px -40% 0px" }
    );

    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return active;
}
function scrollToSection(id) {
  if (id === "home") {
    window.scrollTo({ top: 0, behavior: "smooth" });
  } else {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  }
}

function App() {
  const [showSite, setShowSite] = useState(false);
  const active = useActiveSection();

  // Scroll reveal observer
  useEffect(() => {
    const els = document.querySelectorAll(".scroll-reveal");
    if (!els.length) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("visible"); }),
      { threshold: 0.1 }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  return (
    <div className="bg-[#0a0a0a] text-white font-body">
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: "#141414",
            border: "1px solid rgba(201, 168, 76, 0.2)",
            color: "#fff",
          },
        }}
      />
      {!showSite && <Welcome onEnter={() => setShowSite(true)} />}

      {/* Fixed logo watermark */}
      <div className="fixed inset-0 z-0 flex items-center justify-center pointer-events-none">
        <img
          src={LOGO_IMG}
          alt=""
          className="w-[300px] h-[300px] sm:w-[500px] sm:h-[500px] md:w-[650px] md:h-[650px] lg:w-[800px] lg:h-[800px] object-contain select-none"
          style={{
            opacity: 0.12,
            filter: 'brightness(1.3) contrast(1.1) drop-shadow(0 0 60px rgba(201,168,76,0.2))',
            transform: 'perspective(1000px) rotateX(10deg) rotateY(-6deg) translateZ(50px)',
          }}
        />
      </div>

      <Navbar onNavigate={(i) => scrollToSection(sections[i].id)} pages={sections} currentPage={sections.findIndex((s) => s.id === active)} />

      {/* Floating page selector — desktop */}
      <div data-testid="page-selector" className="fixed right-4 lg:right-6 top-1/2 -translate-y-1/2 z-50 hidden md:flex flex-col items-end gap-1.5">
        {sections.map((sec) => {
          const isActive = active === sec.id;
          return (
            <button
              key={sec.id}
              onClick={() => scrollToSection(sec.id)}
              data-testid={`page-sel-${sec.id}`}
              className={`flex items-center gap-2 transition-all duration-300 rounded-full group ${
                isActive
                  ? "bg-[#c9a84c]/15 border border-[#c9a84c]/40 px-3 py-1.5"
                  : "bg-transparent border border-transparent hover:border-[#c9a84c]/20 hover:bg-[#c9a84c]/5 px-2 py-1.5"
              }`}
            >
              <span className={`text-[10px] uppercase tracking-wider overflow-hidden transition-all duration-300 whitespace-nowrap ${
                isActive ? "text-gold max-w-[90px] opacity-100" : "max-w-0 opacity-0 group-hover:max-w-[90px] group-hover:opacity-100 group-hover:text-zinc-400"
              }`}>{sec.label}</span>
              <div className={`transition-all duration-300 ${isActive ? "w-2.5 h-2.5 rounded-full bg-gold" : "w-1.5 h-1.5 rounded-full bg-zinc-600 group-hover:bg-[#c9a84c]/60"}`} />
            </button>
          );
        })}
      </div>

      {/* Scrollable content */}
      <main className="relative z-[1]">
        <section id="home"><Hero /></section>
        <section id="about" className="scroll-reveal"><About /></section>
        <section id="services" className="scroll-reveal"><Services /></section>
        <section id="documents" className="scroll-reveal"><Documents /></section>
        <section id="pricing" className="scroll-reveal"><Pricing /></section>
        <section id="faq" className="scroll-reveal"><FAQ /></section>
        <section id="contact" className="scroll-reveal"><Contact /></section>
        <section id="newsletter" className="scroll-reveal"><Newsletter /></section>
        <Footer />
      </main>

      {/* Mobile bottom nav */}
      <nav data-testid="mobile-bottom-nav" className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-black/90 backdrop-blur-xl border-t border-[#c9a84c]/15">
        <div className="flex items-center justify-around h-14">
          {sections.map((sec) => (
            <button
              key={sec.id}
              onClick={() => scrollToSection(sec.id)}
              data-testid={`mobile-nav-${sec.id}`}
              className={`flex flex-col items-center gap-0.5 px-1.5 py-1 transition-all ${
                active === sec.id ? "text-gold" : "text-zinc-500"
              }`}
            >
              <div className={`w-1.5 h-1.5 rounded-full ${active === sec.id ? "bg-gold" : "bg-zinc-700"}`} />
              <span className="text-[7px] uppercase tracking-wider">{sec.label}</span>
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
}

export default App;
